/**
 * WelcomeOnboardingModal — full-screen welcome shown on the user's very
 * first login. Backend gate: `should_show_welcome_modal: true`. Once the
 * user clicks the CTA or "Skip for now", the backend records that and the
 * modal never reappears.
 *
 * Existing users (back-filled with onboarding_complete=true at startup)
 * never see this — the gate stays false for them.
 */
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, Target, ArrowRight } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';

export default function WelcomeOnboardingModal() {
  const { state, dismissWelcome, startArchitect } = useOnboarding();
  const navigate = useNavigate();
  const shouldShow = !!state?.should_show_welcome_modal;

  if (!shouldShow) return null;

  async function handleStart() {
    await startArchitect();
    navigate('/industry-architect');
  }

  return (
    <Dialog open={shouldShow} onOpenChange={(open) => { if (!open) dismissWelcome(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0 sm:rounded-lg"
        data-testid="welcome-onboarding-modal"
      >
        {/* Hero band — keeps it distinctive vs the generic "AI slop" white */}
        <div className="bg-gradient-to-br from-[#1A1A1A] via-[#2A2419] to-[#3A2D1A] text-white px-6 sm:px-8 pt-8 pb-6">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 text-xs uppercase tracking-wider text-[#E5C57D] font-medium">
            <Sparkles className="h-3 w-3" />
            Welcome to DH Design
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
            Welcome,{' '}
            <span className="text-[#E5C57D]" data-testid="welcome-first-name">
              {state?.first_name}
            </span>
            .
          </h2>
          <p className="text-sm sm:text-base text-stone-300 mt-3 leading-relaxed">
            DH Design surfaces signal-driven sales intelligence, turns market
            moves into professional proposals, and ships the entire CRM
            workflow your team needs to close — without the noise.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 py-6 bg-white">
          <div className="flex gap-3 items-start">
            <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-[#FAF5E8] flex items-center justify-center">
              <Target className="h-4 w-4 text-[#B89B5E]" />
            </div>
            <div className="text-sm text-stone-700 leading-relaxed">
              <p className="font-medium text-stone-900">
                Start with your Industry Architect.
              </p>
              <p className="mt-1 text-stone-600">
                It tells the platform what signals matter to you — industry,
                ICP, service area, value drivers. Every sweep, suggestion,
                and proposal personalises against this profile.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              type="button"
              onClick={dismissWelcome}
              className="text-sm text-stone-500 hover:text-stone-800 underline-offset-4 hover:underline self-center"
              data-testid="welcome-skip-btn"
            >
              Skip for now
            </button>
            <Button
              onClick={handleStart}
              className="bg-[#1A1A1A] hover:bg-stone-800 text-white w-full sm:w-auto"
              data-testid="welcome-start-btn"
            >
              Set Up My Intelligence Profile
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
